import { DefaultFooterComponent, Label, Popup, TextInput } from '@silverfort/sf-components';
import { ChangeEvent, Dispatch, FC, memo, SetStateAction, useCallback, useState } from 'react';

interface AccountData {
    user: string;
    pass: string;
}

interface AccountPopupContentProps {
    data: AccountData;
    setData: Dispatch<SetStateAction<AccountData>>;
}

interface AddAccountPopupProps {
    open: boolean;
    onClose: () => void;
    onCreate: (user: string, pass: string) => void;
    user: AccountData;
}

const AccountPopupContent: FC<AccountPopupContentProps> = memo(({ data, setData }) => {
    const handleUserChange = useCallback(
        (user: string) => {
            setData((prevData) => ({ ...prevData, user }));
        },
        [setData]
    );

    const handlePasswordChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            setData((prevData) => ({ ...prevData, pass: e.target.value }));
        },
        [setData]
    );

    return (
        <div className="w-full">
            <div className="flex flex-col gap-6">
                <div className="flex justify-between items-center">
                    <Label text="Username" mandatory />
                    <TextInput
                        placeholder="Add username"
                        className="h-8"
                        onChange={handleUserChange}
                        value={data?.user}
                    />
                </div>
                <div className="flex justify-between items-center">
                    <Label text="Password" mandatory />
                    <input
                        type="password"
                        placeholder="Add password"
                        className="h-8 peer font-sf-primary px-3 w-50 rounded-lg border border-sf-gray-200 bg-white text-13 text-sf-gray-800 focus:outline-none focus-visible:border-sf-primary-500 placeholder:text-sf-gray-200"
                        onChange={handlePasswordChange}
                        value={data?.pass}
                    />
                </div>
            </div>
        </div>
    );
});

const AddAccountPopup: FC<AddAccountPopupProps> = memo(({ open, onClose, onCreate, user }) => {
    const [data, setData] = useState<AccountData>({ ...user, pass: '' });

    const onApprove = useCallback(() => {
        onCreate(data.user, data.pass);
    }, [data, onCreate]);

    return (
        <Popup
            open={open}
            popupClassName="!w-[360px]"
            title="Add account credentials"
            ContentComponent={AccountPopupContent}
            ContentComponentProps={{ data, setData }}
            FooterComponent={DefaultFooterComponent}
            FooterComponentProps={{
                primaryButtonProps: { text: user?.user ? 'Update' : 'Create', disabled: !(data.user && data.pass) }
            }}
            onApprove={onApprove}
            onClose={onClose}
        />
    );
});

export default AddAccountPopup;
